<template>
    <div class="actions-editor">
        <div v-if="!localActions || localActions.length === 0" class="empty-actions">
            <div class="empty-icon">
                <i class="material-icons">flash_off</i>
            </div>
            <p>{{ $t("taskModuleRules.noActionsAddedYet") }}</p>
            <a-button type="primary" @click="addAction">
                <i class="material-icons">add</i>
                {{ $t("taskModuleRules.addFirstAction") }}
            </a-button>
        </div>

        <div v-else class="actions-body">
            <draggable v-model="localActions" handle=".drag-handle" @end="emitChange" class="actions-list">
                <div v-for="(action, index) in localActions" :key="`action-${index}`" class="action-card">
                    <div class="action-header">
                        <i class="material-icons drag-handle">drag_indicator</i>
                        <a-select
                            v-model="action.type"
                            class="action-type-select"
                            :placeholder="$t('taskModuleRules.selectAction')"
                            @change="onActionTypeChange(action)"
                        >
                            <a-select-option
                                v-for="opt in actionTypeOptions"
                                :key="opt.value"
                                :value="opt.value"
                            >
                                <span class="action-option">
                                    <i class="material-icons">{{ opt.icon }}</i>
                                    <span>{{ opt.label }}</span>
                                </span>
                            </a-select-option>
                        </a-select>
                        <a-button type="text" size="small" danger @click="removeAction(index)">
                            <i class="material-icons">delete</i>
                        </a-button>
                    </div>

                    <div v-if="action.type && action.type !== 'none'" class="action-config">
                        <AddAssigneeConfig
                            v-if="action.type === 'add-assignee'"
                            v-model="action.addAssignee"
                            @input="emitChange"
                        />

                        <RemoveAssigneeConfig
                            v-if="action.type === 'remove-assignee'"
                            v-model="action.removeAssignee"
                            @input="emitChange"
                        />

                        <ChangeOwnerConfig
                            v-if="action.type === 'change-owner'"
                            v-model="action.changeOwner"
                            @input="emitChange"
                        />

                        <AddFollowerConfig
                            v-if="action.type === 'add-follower'"
                            v-model="action.addFollower"
                            @input="emitChange"
                        />

                        <RemoveFollowerConfig
                            v-if="action.type === 'remove-follower'"
                            v-model="action.removeFollower"
                            @input="emitChange"
                        />

                        <MoveStageConfig
                            v-if="action.type === 'move-stage'"
                            v-model="action.moveStage"
                            :workflow-id="workflowId"
                            @input="emitChange"
                        />

                        <ChangePriorityConfig
                            v-if="action.type === 'change-priority'"
                            v-model="action.changePriority"
                            @input="emitChange"
                        />

                        <AddCollaboratorConfig
                            v-if="action.type === 'add-collaborator'"
                            v-model="action.addCollaborator"
                            @input="emitChange"
                        />

                        <AddTagConfig
                            v-if="action.type === 'add-tag'"
                            v-model="action.addTag"
                            @input="emitChange"
                        />

                        <RemoveTagConfig
                            v-if="action.type === 'remove-tag'"
                            v-model="action.removeTag"
                            @input="emitChange"
                        />

                        <SetDateConfig
                            v-if="action.type === 'set-start-date'"
                            v-model="action.setStartDate"
                            label="Başlangıç Tarihi"
                            @input="emitChange"
                        />

                        <SetDateConfig
                            v-if="action.type === 'set-end-date'"
                            v-model="action.setEndDate"
                            label="Bitiş Tarihi"
                            @input="emitChange"
                        />

                        <CreateChecklistConfig
                            v-if="action.type === 'create-checklist'"
                            v-model="action.createChecklist"
                            @input="emitChange"
                        />

                        <CreateCommentConfig
                            v-if="action.type === 'create-comment'"
                            v-model="action.createComment"
                            @input="emitChange"
                        />

                        <SendEmailConfig
                            v-if="action.type === 'send-email'"
                            v-model="action.sendEmail"
                            @input="emitChange"
                        />

                        <SendWebNotificationConfig
                            v-if="action.type === 'send-web-notification'"
                            v-model="action.sendWebNotification"
                            @input="emitChange"
                        />

                        <WebhookConfig
                            v-if="action.type === 'webhook'"
                            v-model="action.webhook"
                            @input="emitChange"
                        />
                    </div>
                </div>
            </draggable>

            <!-- Add Action Button -->
            <div class="add-action-btn" @click="addAction">
                <i class="material-icons">add_circle</i>
                <span>{{ $t("taskModuleRules.addAction") }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import AddAssigneeConfig from "./AddAssigneeConfig.vue";
import RemoveAssigneeConfig from "./RemoveAssigneeConfig.vue";
import ChangeOwnerConfig from "./ChangeOwnerConfig.vue";
import AddFollowerConfig from "./AddFollowerConfig.vue";
import RemoveFollowerConfig from "./RemoveFollowerConfig.vue";
import MoveStageConfig from "./MoveStageConfig.vue";
import ChangePriorityConfig from "./ChangePriorityConfig.vue";
import AddCollaboratorConfig from "./AddCollaboratorConfig.vue";
import AddTagConfig from "./AddTagConfig.vue";
import RemoveTagConfig from "./RemoveTagConfig.vue";
import SetDateConfig from "./SetDateConfig.vue";
import CreateChecklistConfig from "./CreateChecklistConfig.vue";
import CreateCommentConfig from "./CreateCommentConfig.vue";
import SendEmailConfig from "./SendEmailConfig.vue";
import SendWebNotificationConfig from "./SendWebNotificationConfig.vue";
import WebhookConfig from "./WebhookConfig.vue";
import _ from "lodash";

export default {
    name: "ActionsEditor",
    components: {
        draggable,
        AddAssigneeConfig,
        RemoveAssigneeConfig,
        ChangeOwnerConfig,
        AddFollowerConfig,
        RemoveFollowerConfig,
        MoveStageConfig,
        ChangePriorityConfig,
        AddCollaboratorConfig,
        AddTagConfig,
        RemoveTagConfig,
        SetDateConfig,
        CreateChecklistConfig,
        CreateCommentConfig,
        SendEmailConfig,
        SendWebNotificationConfig,
        WebhookConfig
    },
    props: {
        value: {
            type: Array,
            default: () => []
        },
        whenType: {
            type: String,
            required: true
        },
        workflowId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            localActions: [],
            actionTypeOptions: [
                { value: 'add-assignee', label: 'Assignee Ekle', icon: 'person_add' },
                { value: 'remove-assignee', label: 'Assignee Çıkar', icon: 'person_remove' },
                { value: 'change-owner', label: 'Sahip Değiştir', icon: 'assignment_ind' },
                { value: 'add-follower', label: 'Takipçi Ekle', icon: 'visibility' },
                { value: 'remove-follower', label: 'Takipçi Çıkar', icon: 'visibility_off' },
                { value: 'move-stage', label: 'Stage Taşı', icon: 'arrow_forward' },
                { value: 'change-priority', label: 'Öncelik Değiştir', icon: 'flag' },
                { value: 'add-collaborator', label: 'İşbirlikçi Ekle', icon: 'group_add' },
                { value: 'mark-completed', label: 'Completed İşaretle', icon: 'check_circle' },
                { value: 'mark-done', label: 'Done İşaretle', icon: 'done_all' },
                { value: 'mark-archived', label: 'Arşivle', icon: 'archive' },
                { value: 'add-tag', label: 'Etiket Ekle', icon: 'label' },
                { value: 'remove-tag', label: 'Etiket Çıkar', icon: 'label_off' },
                { value: 'set-start-date', label: 'Başlangıç Tarihi Belirle', icon: 'event_available' },
                { value: 'set-end-date', label: 'Bitiş Tarihi Belirle', icon: 'event' },
                { value: 'create-checklist', label: 'Checklist Oluştur', icon: 'checklist' },
                { value: 'create-comment', label: 'Yorum Ekle', icon: 'comment' },
                { value: 'send-email', label: 'E-posta Gönder', icon: 'email' },
                { value: 'send-web-notification', label: 'Web Bildirimi Gönder', icon: 'notifications' },
                { value: 'webhook', label: 'Webhook Çağır', icon: 'webhook' }
            ]
        };
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal && !_.isEqual(newVal, this.localActions)) {
                    this.localActions = _.cloneDeep(newVal);
                }
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        emitChange() {
            this.$emit("input", _.cloneDeep(this.localActions));
        },

        addAction() {
            const newAction = {
                type: '',
            };
            this.localActions.push(newAction);
            this.emitChange();
        },

        removeAction(index) {
            this.localActions.splice(index, 1);
            this.emitChange();
        },

        onActionTypeChange(action) {
            // Reset config when type changes
            const configKeys = Object.keys(action).filter(k => k !== 'type');
            configKeys.forEach(k => delete action[k]);

            // Initialize config based on type
            if (action.type === 'add-assignee') {
                action.addAssignee = {
                    method: 'static',
                    userList: [],
                    useAllUsers: false,
                    algorithmSettings: {
                        algorithm: 'least_task',
                        dateRange: 'today',
                        checkOnlineStatus: false
                    }
                };
            } else if (action.type === 'remove-assignee') {
                action.removeAssignee = { userList: [] };
            } else if (action.type === 'change-owner') {
                action.changeOwner = { userId: null };
            } else if (action.type === 'add-follower') {
                action.addFollower = { userList: [] };
            } else if (action.type === 'remove-follower') {
                action.removeFollower = { userList: [] };
            } else if (action.type === 'move-stage') {
                action.moveStage = { stageId: null };
            } else if (action.type === 'change-priority') {
                action.changePriority = { priority: 'medium' };
            } else if (action.type === 'add-collaborator') {
                action.addCollaborator = { userList: [] };
            } else if (action.type === 'add-tag') {
                action.addTag = { tagIds: [] };
            } else if (action.type === 'remove-tag') {
                action.removeTag = { tagIds: [] };
            } else if (action.type === 'set-start-date') {
                action.setStartDate = { dateType: 'static', staticDate: null };
            } else if (action.type === 'set-end-date') {
                action.setEndDate = { dateType: 'static', staticDate: null };
            } else if (action.type === 'create-checklist') {
                action.createChecklist = { items: [] };
            } else if (action.type === 'create-comment') {
                action.createComment = { comment: '' };
            } else if (action.type === 'send-email') {
                action.sendEmail = {
                    from: '',
                    toType: 'dynamic',
                    toStatic: [],
                    toDynamic: [],
                    cc: [],
                    bcc: [],
                    subject: '',
                    body: ''
                };
            } else if (action.type === 'send-web-notification') {
                action.sendWebNotification = {
                    message: '',
                    userType: 'dynamic',
                    userStatic: [],
                    userDynamic: []
                };
            } else if (action.type === 'webhook') {
                action.webhook = {
                    url: '',
                    method: 'POST',
                    bodyType: 'full_task',
                    customBody: '',
                    headers: {}
                };
            }

            this.emitChange();
        }
    }
};
</script>

<style lang="scss" scoped>
.actions-editor {
    .empty-actions {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        background: #fafafa;
        border: 2px dashed #d9d9d9;
        border-radius: 12px;

        .empty-icon {
            .material-icons {
                font-size: 64px;
                color: #d9d9d9;
            }
        }

        p {
            margin: 16px 0 24px;
            color: #999;
            font-size: 14px;
        }
    }

    .actions-body {
        .actions-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .action-card {
            background: #fafafa;
            border: 1px solid #e8e8e8;
            border-radius: 8px;
            padding: 16px;

            .action-header {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 16px;

                .drag-handle {
                    cursor: grab;
                    color: #999;
                    font-size: 20px;

                    &:active {
                        cursor: grabbing;
                    }
                }

                .action-type-select {
                    flex: 1;
                    
                    :deep(.ant-select-selection-selected-value) {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        .material-icons {
                            font-size: 18px;
                        }
                    }

                    .action-option {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        .material-icons {
                            font-size: 18px;
                            vertical-align: middle;
                        }
                    }
                }
            }

            .action-config {
                padding: 16px;
                background: #ffffff;
                border-radius: 6px;
            }
        }

        .add-action-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 16px;
            background: #ffffff;
            border: 2px dashed #1890ff;
            border-radius: 8px;
            cursor: pointer;
            color: #1890ff;
            font-weight: 600;
            transition: all 0.3s ease;

            &:hover {
                background: #e6f7ff;
            }

            .material-icons {
                font-size: 24px;
            }
        }
    }
}

:deep(.ant-select-dropdown) {
    .action-option {
        display: flex;
        align-items: center;
        gap: 8px;

        .material-icons {
            font-size: 18px;
            vertical-align: middle;
        }
    }
}
</style>
